/* Options tab — thin parent that composes the sub-components.
   Data (trades, watchlist) lives here so it survives sub-tab switches
   without refetching. The previous 16-rule confirmation gate on the
   Trade Planner has been removed (2026-02) — the Rules sub-tab is now
   a pure reference checklist with no lock/unlock logic.

   Sub-components:
   - OptionsRules          · 16-rule reference checklist (no gating)
   - OptionsTradePlanner   · live-data-driven spread planner
   - OptionsTradeLog       · CRUD over /api/trading/options_trades
   - OptionsWatchlist      · CRUD over /api/trading/options_watchlist
*/

import { useState, useEffect, useCallback } from "react";
import { C } from "./_shared";
import { apiJSON } from "./_optionsShared";
import { useSubTab } from "./_useSubTab";
import OptionsRules        from "./OptionsRules";
import OptionsTradePlanner from "./OptionsTradePlanner";
import OptionsTradeLog     from "./OptionsTradeLog";
import OptionsWatchlist    from "./OptionsWatchlist";
import OptionsAlertLog     from "./OptionsAlertLog";
import OptionsStrategyLibrary from "./OptionsStrategyLibrary";
import OptionsWhaleAlerts   from "./OptionsWhaleAlerts";
import JournalTab          from "./Journal";

// Map Options sub-view keys ↔ URL slugs (deep-linkable).
const OPTIONS_KEY_TO_SLUG = {
  rules: "rules", planner: "trade-planner", log: "trade-log",
  journal: "journal",
  watchlist: "watchlist", alerts: "alert-log",
  library: "strategy-library", whales: "whale-alerts",
};
const OPTIONS_SLUG_TO_KEY = Object.fromEntries(
  Object.entries(OPTIONS_KEY_TO_SLUG).map(([k, v]) => [v, k]),
);

export default function OptionsTab({ journalPrefill, clearJournalPrefill } = {}) {
  const [view, setView] = useSubTab("rules", {
    slugToKey: OPTIONS_SLUG_TO_KEY,
    keyToSlug: OPTIONS_KEY_TO_SLUG,
    parentSlug: "options",
  });
  const [trades, setTrades] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [plannerPrefill, setPlannerPrefill] = useState("");
  const [pendingCount, setPendingCount] = useState(0);

  const refreshTrades = useCallback(async () => {
    try {
      const list = await apiJSON("/api/trading/options_trades");
      setTrades(Array.isArray(list) ? list : []);
    } catch { /* noop */ }
  }, []);
  const refreshWatchlist = useCallback(async () => {
    try {
      const list = await apiJSON("/api/trading/options_watchlist");
      setWatchlist(Array.isArray(list) ? list : []);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { refreshTrades(); refreshWatchlist(); }, [refreshTrades, refreshWatchlist]);

  // Phase 3 — poll the count of pending AI trade approvals so the Trade
  // Planner tab can surface a "waiting for your tap" badge. 30s cadence
  // matches the rest of the suite's live-data polling.
  const refreshPending = useCallback(async () => {
    try {
      const list = await apiJSON("/api/ai/pending-trades");
      setPendingCount(Array.isArray(list) ? list.length : 0);
    } catch { /* noop */ }
  }, []);
  useEffect(() => {
    refreshPending();
    const t = setInterval(refreshPending, 30000);
    return () => clearInterval(t);
  }, [refreshPending]);

  // When a journal prefill arrives from a cross-tab request (e.g. the
  // Portfolio/MarketWatch "Log Trade" action), auto-switch to the Journal
  // sub-tab so the prefill is visible to the user.
  useEffect(() => {
    if (journalPrefill) setView("journal");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalPrefill]);

  // NOTE: The previous client-side TP/SL monitor lived here.
  // It has been replaced by a server-side APScheduler job (5-min cadence
  // during market hours) so alerts fire whether the user has the Options
  // tab open or not. See `run_options_monitor()` in routers/stock.py.

  function goPlanFor(ticker) {
    setPlannerPrefill(ticker);
    setView("planner");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3, gap: 2, width: "fit-content", flexWrap: "wrap" }}>
        {[
          { k: "rules",     l: "📋 Rules" },
          { k: "planner",   l: "🎯 Trade Planner", n: pendingCount || undefined, pending: true },
          { k: "log",       l: "📓 Trade Log",      n: trades.length },
          { k: "journal",   l: "📓 Journal" },
          { k: "watchlist", l: "👁  Watchlist",     n: watchlist.length },
          { k: "alerts",    l: "🔔 Alert Log" },
          { k: "library",   l: "📚 Strategy Library" },
          { k: "whales",    l: "🐋 Whale Alerts" },
        ].map(v => (
          <button key={v.k} onClick={() => setView(v.k)} data-testid={`options-view-${v.k}`}
            style={{ padding: "7px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, background: view === v.k ? "#fff" : "transparent", color: view === v.k ? "#111" : C.gray, boxShadow: view === v.k ? "0 1px 3px rgba(0,0,0,0.08)" : "none", display: "flex", alignItems: "center", gap: 6 }}>
            {v.l}
            {v.n !== undefined && (
              v.pending
                ? <span data-testid="options-pending-badge" style={{ fontSize: 10, padding: "1px 7px", borderRadius: 10, background: C.ab, color: C.at, fontWeight: 700 }}>⏳ {v.n}</span>
                : <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, background: view === v.k ? C.ib : C.gbg, color: view === v.k ? C.it : C.gray, fontWeight: 700 }}>{v.n}</span>
            )}
          </button>
        ))}
      </div>

      {view === "rules"     && <OptionsRules        />}
      {view === "planner"   && <OptionsTradePlanner prefilledTicker={plannerPrefill} onLogTrade={refreshTrades} />}
      {view === "log"       && <OptionsTradeLog     trades={trades} refreshTrades={refreshTrades} />}
      {view === "journal"   && <JournalTab          journalPrefill={journalPrefill} clearJournalPrefill={clearJournalPrefill} />}
      {view === "watchlist" && <OptionsWatchlist    watchlist={watchlist} refreshWatchlist={refreshWatchlist} onAnalyse={goPlanFor} />}
      {view === "alerts"    && <OptionsAlertLog     />}
      {view === "library"   && <OptionsStrategyLibrary onUseStrategy={(s) => { goPlanFor(""); }} />}
      {view === "whales"    && <OptionsWhaleAlerts   />}
    </div>
  );
}
